import type { CSSProperties } from "react";
import type { DecisionMode, EmitRule, ScoreBand } from "@/types/decision-definition-schema";

export interface HubOutputHandle {
  id: string;
  label: string;
  color: string;
}

const OUTCOME_COLORS = [
  "#22c55e",
  "#f59e0b",
  "#ef4444",
  "#3b82f6",
  "#a855f7",
  "#14b8a6",
];

/** Canonical tri-state / boolean outcome rank for layout order. */
function outcomeRank(rule: EmitRule): number {
  const key = `${rule.emit?.outcome || ""} ${rule.label || ""}`.toLowerCase();
  if (/approve|yes|true|pass|allow/.test(key)) return 0;
  if (/review|unknown|manual|pending/.test(key)) return 1;
  if (/decline|no|false|deny|fail|reject|block/.test(key)) return 2;
  return 50;
}

/**
 * Sort emit rules into layout order: approve → review → decline (then others).
 */
export function sortEmitRulesForLayout(emitRules: EmitRule[]): EmitRule[] {
  return [...emitRules].sort((a, b) => {
    const diff = outcomeRank(a) - outcomeRank(b);
    if (diff !== 0) return diff;
    return (a.label || "").localeCompare(b.label || "");
  });
}

/**
 * Output source handles for the Decision hub, driven by decision mode.
 *
 * Handle order matches output-node layout order (top→bottom after dagre
 * compensation): approve / review / decline.
 */
export function getHubOutputHandles(
  mode: DecisionMode = "tri_state",
  emitRules?: EmitRule[],
  scoreBands?: ScoreBand[]
): HubOutputHandle[] {
  switch (mode) {
    case "boolean":
      return [
        { id: "yes", label: "Approve", color: "#22c55e" },
        { id: "no", label: "Decline", color: "#ef4444" },
      ];
    case "tri_state":
      return [
        { id: "yes", label: "Approve", color: "#22c55e" },
        { id: "unknown", label: "Review", color: "#f59e0b" },
        { id: "no", label: "Decline", color: "#ef4444" },
      ];
    case "multi_outcome": {
      const ordered = sortEmitRulesForLayout(emitRules || []);
      if (ordered.length > 0) {
        return ordered.map((rule, i) => ({
          id: `outcome-${rule.id}`,
          label: rule.label || rule.emit?.outcome || `Outcome ${i + 1}`,
          color: OUTCOME_COLORS[i % OUTCOME_COLORS.length],
        }));
      }
      return [{ id: "outcome", label: "Outcome", color: "#8b5cf6" }];
    }
    case "scorecard": {
      if (scoreBands && scoreBands.length > 0) {
        return scoreBands.map((band, i) => ({
          id: `band-${i}`,
          label: `${band.label} (${band.min}–${band.max})`,
          color: OUTCOME_COLORS[i % OUTCOME_COLORS.length],
        }));
      }
      return [{ id: "score", label: "Score", color: "#3b82f6" }];
    }
    default:
      return [
        { id: "yes", label: "Approve", color: "#22c55e" },
        { id: "no", label: "Decline", color: "#ef4444" },
      ];
  }
}

/**
 * Evenly space N handles along the output edge.
 * Handle[0] is placed near the top (LR) or left (TB) so it aligns with the
 * first successor node that dagre stacks in insertion order.
 */
export function getDistributedHandleOffset(
  isHorizontal: boolean,
  index: number,
  total: number
): CSSProperties {
  if (total <= 1) {
    return isHorizontal ? { top: "50%" } : { left: "50%" };
  }
  const pct = ((index + 1) / (total + 1)) * 100;
  return isHorizontal ? { top: `${pct}%` } : { left: `${pct}%` };
}

/**
 * Wire emit rule i → handle i (same order as getHubOutputHandles / layout).
 * Avoids semantic remapping that puts Review on the Decline port, etc.
 */
export function resolveEmitSourceHandle(
  mode: DecisionMode,
  emitRule: EmitRule,
  index: number,
  handles: HubOutputHandle[]
): string {
  if (handles.length === 0) return "yes";

  if (mode === "multi_outcome") {
    const match = handles.find((h) => h.id === `outcome-${emitRule.id}`);
    return match?.id ?? handles[Math.min(index, handles.length - 1)].id;
  }

  if (mode === "scorecard") {
    return handles[Math.min(index, handles.length - 1)].id;
  }

  // boolean / tri_state — strict index order (approve, review, decline)
  return handles[Math.min(index, handles.length - 1)].id;
}
